import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { EPageContentBackgroundColor } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { BackButton } from '@/modules/website-builder/components/BackButton';
import {
  extractEmbedUrl,
  validateEmbedUrl,
} from '@/modules/website-builder/embed-url';
import { WebsiteBuilderPageFormValues } from '@/modules/website-builder/types';

export const EmbedEditor: React.FC<{
  index: number;
  defaultName: string;
  onClickBack: () => void;
  methods: UseFormReturn<WebsiteBuilderPageFormValues, any>;
}> = (props) => {
  const { control, register, watch } = props.methods;
  const urlValue = watch(`content.${props.index}.url`);
  const urlValidation = validateEmbedUrl(urlValue ?? '');

  return (
    <Stack spacing="4">
      <BackButton onClick={props.onClickBack}>
        Back to all components
      </BackButton>

      <Card as={Stack} spacing="4" p="6">
        <Text textStyle="bodyStrong">Component Settings</Text>

        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input
            placeholder="Embed"
            {...register(`content.${props.index}.name`)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Background Color</FormLabel>
          <Select
            {...register(`content.${props.index}.options.backgroundColor`)}
          >
            <option value={EPageContentBackgroundColor.WHITE}>White</option>
            <option value={EPageContentBackgroundColor.TRANSPARENT}>
              Light Gray
            </option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Height (px)</FormLabel>
          <Input
            type="number"
            min={120}
            max={1200}
            {...register(`content.${props.index}.options.height`, {
              valueAsNumber: true,
            })}
          />
          <FormHelperText>
            Recommended 300–500 for countdown timers.
          </FormHelperText>
        </FormControl>
      </Card>

      <Card as={Stack} spacing="4" p="6">
        <Text textStyle="bodyStrong">Embed</Text>

        <Controller
          control={control}
          name={`content.${props.index}.url`}
          render={({ field }) => (
            <FormControl isInvalid={!!field.value && !urlValidation.ok}>
              <FormLabel>Embed URL</FormLabel>
              <Textarea
                resize="vertical"
                minH="28"
                placeholder={`Paste an HTTPS URL or iframe code, e.g.\n<iframe src="https://www.tickcounter.com/..."></iframe>`}
                value={field.value ?? ''}
                onChange={(event) => {
                  field.onChange(event.target.value);
                }}
                onPaste={(event) => {
                  const pasted =
                    event.clipboardData.getData('text/plain') ||
                    event.clipboardData.getData('text');
                  if (!pasted) {
                    return;
                  }
                  event.preventDefault();
                  const extracted = extractEmbedUrl(pasted);
                  field.onChange(extracted);
                }}
                onBlur={(event) => {
                  const extracted = extractEmbedUrl(event.target.value);
                  if (extracted !== event.target.value) {
                    field.onChange(extracted);
                  }
                  field.onBlur();
                }}
              />
              <FormHelperText>
                Paste an HTTPS URL or an iframe snippet. We extract the{' '}
                <code>src</code> automatically. YouTube watch / share links are
                converted to embed URLs. Only allowlisted hosts are supported
                (e.g. TickCounter, TimeAndDate, YouTube, Vimeo, Calendly).
              </FormHelperText>
              {!!field.value && !urlValidation.ok && (
                <FormErrorMessage>{urlValidation.reason}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
      </Card>
    </Stack>
  );
};
